import { useQueryClient } from '@tanstack/react-query';
import { Field, Formik } from 'formik';
import { type FormikConfig } from 'formik/dist/types';
import { useRouter } from 'next/router';
import { memo, useCallback, useState } from 'react';
import { useMount } from 'react-use';
import * as Yup from 'yup';

import { Account, Auth } from '@air/api';
import { WORKSPACE_INTEGRATION_SSO_NOT_ENABLED } from '@air/errors';
import { FormikField } from '@air/formik-field';
import { Info } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { Label } from '@air/primitive-label';
import { Tooltip } from '@air/primitive-tooltip';
import { callOnFullAccountUserPool, getProviderNameFromSSOOptions } from '@air/utils-auth';
import { isAirrorInstance, reportErrorToBugsnag } from '@air/utils-error';

import Form from '~/components/Form';
import { Routes } from '~/constants/routes';
import { CONTINUE_WITH_PASSWORD, LOGIN_EMAIL_INPUT, LOGIN_SUBMIT_BUTTON, SAML_SSO_FORM } from '~/constants/testIDs';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { getAccountsSSOOptionsKey } from '~/hooks/useGetAccountsSSOOptions';
import { sanitizeEmail } from '~/utils/EmailUtils';

type SSOLoginSchemaValues = {
  email: string;
  rememberEmail: boolean;
};

const SSOLoginSchema = Yup.object()
  .shape({
    email: Yup.string()
      .email('Please enter a valid email address.')
      .required('Please enter a valid email address.')
      .default(''),
    rememberEmail: Yup.boolean().default(false),
  })
  .required();

export type SamlSSOLoginFormProps = {
  redirect?: string;
  onContinueWithPasswordClick: () => void;
  onRedirect?: () => void;
  customState: object | undefined;
};

const SAML_SSO_LAST_USED_EMAIL_KEY = 'saml-sso-last-used-email';

export const SamlSSOLoginForm = memo(
  ({ customState, onContinueWithPasswordClick, onRedirect, redirect }: SamlSSOLoginFormProps) => {
    const { maintenanceModeJson, selectWorkspaceAuth } = useFeatureFlags();
    const isFormDisabled = !!maintenanceModeJson?.maintenanceMessage;
    const { push } = useRouter();
    const queryClient = useQueryClient();
    const [lastUsedEmail, setLastUsedEmail] = useState('');

    useMount(() => {
      const lastUsedEmail = localStorage.getItem(SAML_SSO_LAST_USED_EMAIL_KEY);

      if (lastUsedEmail) {
        setLastUsedEmail(lastUsedEmail);
      }
    });

    const onSubmit: FormikConfig<SSOLoginSchemaValues>['onSubmit'] = useCallback(
      async (values, { setSubmitting, setErrors }) =>
        callOnFullAccountUserPool(async () => {
          const userEmail = sanitizeEmail(values.email);
          const rememberEmail = values.rememberEmail;

          if (rememberEmail) {
            localStorage.setItem(SAML_SSO_LAST_USED_EMAIL_KEY, userEmail);
          } else {
            localStorage.removeItem(SAML_SSO_LAST_USED_EMAIL_KEY);
          }

          try {
            setSubmitting(true);
            const result = await Account.getAccountSSOOptionsV2({ email: userEmail });

            if (isAirrorInstance(result) || result.data.length === 0) {
              queryClient.setQueryData(
                getAccountsSSOOptionsKey(userEmail),
                isAirrorInstance(result) ? [] : result.data,
              );
              setSubmitting(false);
              setErrors({
                email: WORKSPACE_INTEGRATION_SSO_NOT_ENABLED.message,
              });
            } else {
              queryClient.setQueryData(getAccountsSSOOptionsKey(userEmail), result.data);
              const userEmailDomain = userEmail.split('@')[1];
              const domains = selectWorkspaceAuth?.domains;

              if (domains?.includes(userEmailDomain) && result.data.length > 1) {
                push({
                  pathname: Routes.selectSSOProviderToLoginTo,
                  query: {
                    email: userEmail,
                    customState: customState ? JSON.stringify(customState) : undefined,
                    redirect,
                  },
                });
              } else {
                const ssoOption = result.data[0];

                try {
                  const samlProvider = getProviderNameFromSSOOptions(ssoOption);

                  if (samlProvider) {
                    Auth.ssoSamlSignin(samlProvider, {
                      ...(customState ?? {}),
                      redirect,
                    });
                  } else {
                    throw new Error(`Missing samlProvider!`);
                  }

                  onRedirect?.();
                } catch (error) {
                  reportErrorToBugsnag({
                    error,
                    context: 'Failed login user using saml sso',
                    metadata: { ssoOption },
                  });

                  setErrors({ email: 'An error occurred. Please try again later' });
                }
              }
            }
          } catch (error) {
            setSubmitting(false);
            reportErrorToBugsnag({
              error,
              context: 'Failed get account SSO options',
              metadata: { updatedAccount: { email: userEmail } },
            });

            setErrors({ email: 'An error occurred. Please try again later' });
          }
        }),
      [customState, onRedirect, push, queryClient, redirect, selectWorkspaceAuth?.domains],
    );

    return (
      <Formik
        initialValues={SSOLoginSchema.cast({ email: lastUsedEmail, rememberEmail: !!lastUsedEmail })}
        validationSchema={SSOLoginSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <>
            <Form data-testid={SAML_SSO_FORM}>
              <FormikField
                className="mb-6"
                component={
                  <Input
                    placeholder="Add your email"
                    type="email"
                    autoComplete="username"
                    data-testid={LOGIN_EMAIL_INPUT}
                    disabled={isFormDisabled}
                    size="large"
                  />
                }
                label="Work email"
                name="email"
                id="email"
              />
              <Label className="mb-8 flex cursor-default flex-row items-center gap-1">
                <Field id="rememberEmail" type="checkbox" name="rememberEmail" />
                Remember email on this device
                <Tooltip label="We'll remember your email next time you log in so you don't have to enter it again.">
                  <Info className="size-3 text-grey-10" />
                </Tooltip>
              </Label>
              <Button
                type="submit"
                size="large"
                className="w-full"
                isLoading={isSubmitting}
                data-testid={LOGIN_SUBMIT_BUTTON}
                disabled={isFormDisabled}
              >
                Log in
              </Button>
            </Form>
            <div className="mt-5 flex w-full justify-center">
              <button
                data-testid={CONTINUE_WITH_PASSWORD}
                className="font-medium text-14 text-grey-10 underline hover:text-grey-12"
                onClick={onContinueWithPasswordClick}
                type="button"
              >
                Continue with Google or password
              </button>
            </div>
          </>
        )}
      </Formik>
    );
  },
);

SamlSSOLoginForm.displayName = 'SamlSSOLoginForm';
